'use client';
import { useState, useEffect } from 'react';
import { z } from 'zod';
import Link from 'next/link';

import { fetchJson } from '@/lib/client/fetchJson';
import { sortedCountries } from '@/shared/sortedCountries';
import FeatureFlag from '@/shared/features';

import Modal from '../atoms/Modal';
import Input from '../atoms/Input';
import Label from '../atoms/Label';
import { ArrowDown } from '../atoms/Icons';
import UIButton from '../atoms/UIButtons/UIButton';
import Checkbox from '../atoms/Checkbox';
import GoogleLoginButton from './GoogleLoginButton';

const EMAIL_HELP =
  'If your account is in our system, you will receive a login link by email within a few minutes. If you do not see it, please check your spam or junk folder.';
const CREATE_HELP = 'Creating an account will send a confirmation link to your email address.';
const emailSchema = z.string().trim().email('Enter a valid email address.');

export default function LoginModal({ onClose }) {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('USA');
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(false);
  const [isCyclist, setIsCyclist] = useState(false);
  const [isAdult, setIsAdult] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [success, setSuccess] = useState(false);

  // Auto-close modal 2 seconds after success
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => onClose(), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, onClose]);

  const validate = () => {
    const errors = {};
    if (showCreate && !name.trim()) {
      errors.name = 'Name is required.';
    }
    if (showCreate && !displayName.trim()) {
      errors.displayName = 'Display Name is required.';
    }
    if (!email.trim()) {
      errors.email = 'Email is required.';
    } else {
      const emailResult = emailSchema.safeParse(email);
      if (!emailResult.success) {
        errors.email = emailResult.error.issues[0]?.message || 'Enter a valid email address.';
      }
    }

    if (showCreate && !isAdult) {
      errors.isAdult = 'You must be 18 or older to create an account.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsProcessing(true);
    setMessages([]);

    const payload = showCreate
      ? {
          name: name.trim(),
          displayName: displayName.trim(),
          email: email.trim(),
          country,
          showOnLeaderboard,
          isCyclist,
          isAdult,
        }
      : { 
          email: email.trim(),
          returnUrl: window.location.pathname + window.location.search
      };
    const endpoint = showCreate ? '/api/auth2/create' : '/api/auth2/email-link';

    try {
      const res = await fetchJson(endpoint, { method: 'POST', body: payload });
      if (res?.success) {
        setMessages([
          {
            type: 'success',
            text: showCreate
              ? 'If your account is in our system, you will receive a login link by email within a few minutes. If you do not see it, please check your spam or junk folder.'
              : EMAIL_HELP,
          },
        ]);
        setEmail('');
        setSuccess(true);
      } else {
        setMessages([{ type: 'error', text: res?.message || 'Request failed.' }]);
      }
    } catch (err) {
      const errMessage = err?.body?.message || err?.message || 'Request failed.';
      setMessages([{ type: 'error', text: errMessage }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleCreate = () => {
    setSuccess(false);
    setShowCreate((prev) => !prev);
    setFieldErrors({});
    setMessages([]);
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <h2 className="text-xl lg:text-2xl xl:text-[26px] text-center font-semibold text-[#323232] mb-3">
            {showCreate ? 'Create account' : 'Sign in'}
          </h2>
          <p className="text-sm lg:text-base text-[#626262] font-normal">
            {showCreate ? CREATE_HELP : ''}
          </p>
          {!showCreate && (
              <div>
                <p className="text-sm lg:text-base text-[#626262] font-normal pt-[1em]">Benefits of creating an account and logging in:</p>
                <ul className="
                    list-image-[url('data:image/svg+xml;base64,PHN2ZwogICBmaWxsPSIjMDBlYWZkIgogICB3aWR0aD0iMTYiCiAgIGhlaWdodD0iMTIiCiAgIHZpZXdCb3g9IjAgMCAxNiAxMiIKICAgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIgogICB4bWxuczpzdmc9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8ZGVmcwogICAgIGlkPSJkZWZzMSIgLz4KICA8cGF0aAogICAgIGQ9Ik0gNC45Njk5MjAxLDkuNDA2Mzk1OSAxLjI2MjQ4NDMsNS42OTg5NTk5IDAsNi45NTI1NTM5IDQuOTY5OTIwMSwxMS45MjI0NzMgMTUuNjM4ODAxLDEuMjUzNTkzNCAxNC4zODUyMDcsMCBaIiAgICAgCiAgICAgc3R5bGU9InN0cm9rZS13aWR0aDowLjg4OTA3NCIgLz4KPC9zdmc+Cg==')] 
                    pl-8 my-2 pb-[1em]
                    font-bold space-y-[0.5em]">
                  <li>Save hearts</li>
                  <li>Check in earn your spot on the leaderboard</li>
                  <li>Share your photos </li>
                </ul> 
              </div>
            )}
        </div>

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-3">
            {showCreate && (
              <div className="grid md:gap-3 gap-4">
                <Input
                  label="Full Name* (won't show on public leaderboard)"
                  labelClass="!text-xs !text-[#232323]/70 !leading-[1]"
                  className="min-h-11"
                  name="name"
                  id="visitorModal_Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={Boolean(fieldErrors.name)}
                  errorMessage={fieldErrors.name}
                />
                <Input
                  label="Display Name*"
                  labelClass="!text-xs !text-[#232323]/70 !leading-[1]"
                  className="min-h-11"
                  name="displayName"
                  id="visitorModal_DisplayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  error={Boolean(fieldErrors.displayName)}
                  errorMessage={fieldErrors.displayName}
                />
              </div>
            )}
            <Input
              label="Email Address*"
              labelClass="!text-xs !text-[#232323]/70 !leading-[1]"
              className="min-h-11"
              name="email"
              id="visitorModal_Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(fieldErrors.email)}
              errorMessage={fieldErrors.email}
            />
            {showCreate && (
              <>
                <div className="flex flex-col gap-1">
                  <Label className="!text-xs !text-[#232323]/70 !leading-[1]">Country*</Label>
                  <div className="relative">
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="w-full rounded-md appearance-none border border-[#DDDDDD] text-[#232323] bg-white pl-4 leading-[1] pr-10 py-2 h-11 text-base font-medium outline-1 -outline-offset-1 outline-gray-300 focus:outline-1 focus:-outline-offset-1 focus:outline-[#00EAFD]"
                    >
                      {sortedCountries.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <ArrowDown className="pointer-events-none absolute inset-y-0 right-4 m-auto" />
                  </div>
                </div>

                <div className="pt-4 m-0">
                  <Checkbox
                    options={[
                      {
                        label: (
                          <span className="text-[#626262] text-sm leading-[1.5] font-normal">
                            I&lsquo;m a Heart Hunter! I agree to show my display name, country, and
                            initials on the{' '}
                            <Link href="/leaderboard" className="text-[#626262] underline">
                              public leaderboard
                            </Link>
                            . I understand I can change this preference at any time via the My
                            Account page.
                          </span>
                        ),
                        value: 'showOnLeaderboard',
                      },
                    ]}
                    className="items-start"
                    checkBoxClass="mt-1"
                    checkedColor="bg-[#00EAFD]"
                    checkColor="text-[#323232]"
                    name="showOnLeaderboard"
                    selectedValues={showOnLeaderboard ? ['showOnLeaderboard'] : []}
                    onChange={(vals) => {
                      const isChecked = vals.includes('showOnLeaderboard');
                      setShowOnLeaderboard(isChecked);
                      if (!isChecked) {
                        setIsCyclist(false);
                      }
                    }}
                  />
                </div>

                <div className={!showOnLeaderboard ? 'opacity-90' : ''}>
                  <Checkbox
                    options={[
                      {
                        label: (
                          <span className="text-[#626262] text-sm leading-[1.5] font-normal">
                            I&apos;m exploring by bike! Show me as a cyclist on the Leaderboard.
                          </span>
                        ),
                        value: 'isCyclist',
                      },
                    ]}
                    name="isCyclist"
                    checkedColor="bg-[#00EAFD]"
                    checkColor="text-[#323232]"
                    className="items-start"
                    checkBoxClass="mt-1"
                    selectedValues={isCyclist ? ['isCyclist'] : []}
                    onChange={(vals) => {
                      if (showOnLeaderboard) {
                        setIsCyclist(vals.includes('isCyclist'));
                      }
                    }}
                    disabled={!showOnLeaderboard}
                  />
                </div>

                <div>
                  <Checkbox
                    options={[
                      {
                        label: (
                          <span className="text-[#626262] text-sm leading-[1.5] font-normal">
                            I confirm that I am 18 years of age or older.
                          </span>
                        ),
                        value: 'isAdult',
                      },
                    ]}
                    name="isAdult"
                    checkedColor="bg-[#00EAFD]"
                    checkColor="text-[#323232]"
                    className="items-start"
                    checkBoxClass="mt-1"
                    selectedValues={isAdult ? ['isAdult'] : []}
                    onChange={(vals) => setIsAdult(vals.includes('isAdult'))}
                    error={Boolean(fieldErrors.isAdult)}
                    errorMessage={fieldErrors.isAdult}
                  />
                </div>
              </>
            )}

            <div>
              <UIButton
                type="submit"
                rounded="full"
                className="mx-auto max-w-[343px] w-full block"
                variant="primaryTheme"
                disabled={isProcessing}
              >
                {isProcessing
                  ? 'Please wait...'
                  : showCreate
                    ? 'Create Account'
                    : 'Send a One-Time Link'}
              </UIButton>
            </div>
          </form>
        )}
        <FeatureFlag featureName="googleAuth">
          {!showCreate && (
            <GoogleLoginButton
              onSuccess={() => setSuccess(true)}
              onError={(msg) => setMessages([{ type: 'error', text: msg }])}
            />
          )}
        </FeatureFlag>
        {messages.length > 0 && (
          <div className="space-y-2">
            {messages.map((message, idx) => (
              <p
                key={`${message.type}-${idx}`}
                className={
                  message.type === 'success' ? 'text-sm text-green-700' : 'text-sm text-red-600'
                }
              >
                {message.text}
              </p>
            ))}
          </div>
        )}

        <div className={`text-sm text-[#626262] text-center font-normal pt-3`}>
          {showCreate ? 'Already have an account?' : 'Need an account?'}{' '}
          <button
            type="button"
            onClick={toggleCreate}
            className="text-[#626262] hover:text-[#646464] lg:hover:bg-[#64646413] border border-[#626262] py-1 px-2 cursor-pointer inline font-normal rounded-[50px] transition-all duration-200"
          >
            {showCreate ? 'Sign in' : 'Create one'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
